import { useState, useEffect } from "react";
import { Stack, DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, Spinner, SpinnerSize } from "@fluentui/react";
import { getFunctions } from "../../../api/admin/api_admin";
import { FunctionDetail, FunctionsResponse } from "../../../api/admin/models_admin";
import { AdminLabel, LinkButton, CellContent, formatDateTime, ErrorDialog } from "../components";

export const Functions = () => {
    const [functionsData, setFunctionsData] = useState<FunctionsResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchFunctions();
    }, []);

    const fetchFunctions = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getFunctions();
            if ("error" in data) {
                setError(data.error);
            } else {
                setFunctionsData(data);
            }
        } catch (err) {
            setError("機能情報の取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const canModify = (): boolean => {
        if (!functionsData) return false;
        const modifyFunc = functionsData.functions_dict['modify_function'];
        return modifyFunc && 
               functionsData.current_user.authority_level >= modifyFunc.need_authority_level;
    };

    const actionWidth = 30 + (canModify() ? 60 : 0)
    const columns: IColumn[] = [
        {
            key: 'actions',
            name: '',
            minWidth: actionWidth,
            maxWidth: actionWidth,
            onRender: (func: FunctionDetail) => (
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    {canModify() && (
                        <LinkButton 
                            link_to={`/menu/functions/modify?function_id=${func.FNC_function_id}`} 
                            text="変更" 
                            styles={{ root: { minWidth: 60 } }} 
                        />
                    )}
                </Stack>
            )
        },
        {
            key: 'function_id',
            name: '機能ID',
            fieldName: 'FNC_function_id',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            onRender: (func: FunctionDetail) => (
                <CellContent>{func.FNC_function_id}</CellContent>
            )
        },
        {
            key: 'function_name',
            name: '機能名',
            fieldName: 'FNC_function_name',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            onRender: (func: FunctionDetail) => (
                <CellContent>{func.FNC_function_name}</CellContent>
            )
        },
        {
            key: 'need_authority_level',
            name: '必要権限レベル',
            fieldName: 'FNC_need_authority_level',
            minWidth: 120,
            maxWidth: 150,
            isResizable: true,
            onRender: (func: FunctionDetail) => (
                <CellContent>{func.FNC_need_authority_level}</CellContent>
            )
        },
        {
            key: 'entry_datetime',
            name: '登録日時',
            fieldName: 'FNC_entry_datetime',
            minWidth: 150,
            maxWidth: 180,
            isResizable: true,
            onRender: (func: FunctionDetail) => (
                <CellContent>{formatDateTime(func.FNC_entry_datetime)}</CellContent>
            )
        },
        {
            key: 'update_datetime',
            name: '更新日時',
            fieldName: 'FNC_update_datetime',
            minWidth: 150,
            maxWidth: 180,
            isResizable: true,
            onRender: (func: FunctionDetail) => (
                <CellContent>{formatDateTime(func.FNC_update_datetime)}</CellContent>
            )
        }
    ];

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="機能一覧" />

            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <DetailsList
                items={functionsData?.functions || []}
                columns={columns}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.none}
                isHeaderVisible={true}
            />
        </Stack>
    );
}